import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, Check, Download, AlertTriangle } from 'lucide-react';
import styles from './QrModal.module.css';

export default function QrModal({ eventName, eventCode, joinUrl, onClose }) {
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [copied, setCopied] = useState(false);

    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=10&color=0a0a1a&bgcolor=ffffff&data=${encodeURIComponent(joinUrl)}`;

    /* Close on ESC */
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);

    const handleCopyUrl = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(joinUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* fallback: select the input text */
        }
    }, [joinUrl]);

    const handleDownload = async () => {
        const url = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=20&color=0a0a1a&bgcolor=ffffff&data=${encodeURIComponent(joinUrl)}`;
        const fileName = (eventCode || eventName || 'event').replace(/[^a-zA-Z0-9-_]/g, '-');
        try {
            const res = await fetch(url);
            const blob = await res.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `livepulse-${fileName}-qr.png`;
            link.click();
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('QR download failed:', err);
        }
    };

    return (
        <div
            className={styles.overlay}
            role="dialog"
            aria-modal="true"
            aria-label="QR code modal"
        >
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

                {/* ── Header ── */}
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <h3 className={styles.eventName}>{eventName || 'Event'}</h3>
                        <div className={styles.headerMeta}>
                            {eventCode && (
                                <span className={styles.codePill}>#{eventCode}</span>
                            )}
                            <span className={styles.scanLabel}>Scan to join</span>
                        </div>
                    </div>
                    <button
                        type="button"
                        className={styles.closeBtn}
                        onClick={onClose}
                        aria-label="Close"
                    >
                        <X size={15} />
                    </button>
                </div>

                {/* ── QR image ── */}
                <div className={styles.qrSection}>
                    <div className={styles.qrFrame}>
                        {/* Loading skeleton + spinner */}
                        {isLoading && !hasError && (
                            <>
                                <div className={styles.qrSkeleton} />
                                <div className={styles.qrSpinner} />
                            </>
                        )}

                        {/* Error state */}
                        {hasError && (
                            <div className={styles.qrError}>
                                <AlertTriangle size={28} className={styles.qrErrorIcon} aria-hidden />
                                <p className={styles.qrErrorText}>
                                    Could not load QR code.<br />Check your internet connection.
                                </p>
                            </div>
                        )}

                        {/* Actual QR image (hidden until loaded) */}
                        {!hasError && (
                            <img
                                src={qrSrc}
                                alt={`QR code for ${eventName}`}
                                className={`${styles.qrImage} ${isLoading ? styles.qrImageHidden : ''}`}
                                onLoad={() => setIsLoading(false)}
                                onError={() => { setIsLoading(false); setHasError(true); }}
                            />
                        )}
                    </div>

                    {/* URL bar */}
                    <div className={styles.urlBar}>
                        <span className={styles.urlText} title={joinUrl}>{joinUrl}</span>
                        <button
                            type="button"
                            className={`${styles.copyUrlBtn}${copied ? ` ${styles.copied}` : ''}`}
                            onClick={handleCopyUrl}
                            aria-label="Copy link"
                        >
                            {copied ? <Check size={11} /> : <Copy size={11} />}
                            {copied ? 'Copied!' : 'Copy'}
                        </button>
                    </div>
                </div>

                {/* ── Footer ── */}
                <div className={styles.footer}>
                    <span className={styles.footerHint}>
                        Share this QR at your event for instant access.
                    </span>
                    <button
                        type="button"
                        className={styles.downloadBtn}
                        onClick={handleDownload}
                        disabled={isLoading || hasError}
                    >
                        <Download size={13} />
                        Download
                    </button>
                </div>

            </div>
        </div>
    );
}
